import './S3FileUploader.css';
import React, {useEffect, useState, useCallback} from "react";
import PropTypes from 'prop-types';
import {Auth, Storage} from 'aws-amplify';
import FileUploader from "devextreme-react/file-uploader";
import deMessages from "devextreme/localization/messages/de.json";
import {loadMessages, locale} from "devextreme/localization";
import notify from "devextreme/ui/notify";
import {useTranslation} from "react-i18next";
import { SFNClient, ListExecutionsCommand, GetActivityTaskCommand, SendTaskSuccessCommand } from "@aws-sdk/client-sfn";
import awsconfig from '../aws-exports';
import { custom } from 'devextreme/ui/dialog';

const csvStateMachinArn = process.env.REACT_APP_CSV_STATE_MACHINE_ARN;
const csvStateMachineApproveActivityArn = process.env.REACT_APP_CSV_STATE_MACHINE_APPROVE_ACTIVITY_ARN;

// Provide credentials lazily for AWS SDK v3. This avoids invalid/expired creds and ensures the
// resolved object has only the essential keys (accessKeyId, secretAccessKey, sessionToken).
const client = new SFNClient({
    region: awsconfig.aws_project_region,
    credentials: async () => Auth.currentCredentials().then((c) => Auth.essentialCredentials(c)),
});

export const confirmImportDialog = (input) => {
    const {existingCount = 0, newCount = 0, reassignCount = 0} = input;

    const parts = [];

    if (existingCount === 1) {
        parts.push('1 Kombination aus "SN water meter" und "DevEUI" ist bereits vorhanden.');
    } else if (existingCount > 1) {
        parts.push(`${existingCount} Kombinationen aus "SN water meter" und "DevEUI" sind bereits vorhanden.`);
    }

    if (newCount === 1) {
        parts.push('1 neue Kombination aus "SN water meter" und "DevEUI" wird hinzugefügt.');
    } else if (newCount > 1) {
        parts.push(`${newCount} neue Kombinationen aus "SN water meter" und "DevEUI" werden hinzugefügt.`);
    }

    if (reassignCount === 1) {
        parts.push('1 "DevEUI" ist bereits einer anderen "SN water meter" zugeordnet und wird bei Bestätigung umgehängt.');
    } else if (reassignCount > 1) {
        parts.push(`${reassignCount} DevEUIs sind bereits anderen SN water metern zugeordnet und werden bei Bestätigung umgehängt.`);
    }

    const messageHtml = parts.length ? parts.map(p => `<p>${p}</p>`).join('') : '<p>Keine Änderungen.</p>';

    const canImport = existingCount === 0 || newCount > 0 || reassignCount > 0;

    let dialog = custom({
        title: "Import Bestätigung",
        messageHtml,
        buttons: canImport ? [
            {
                stylingMode: 'outlined',
                hoverStateEnabled: true,
                activeStateEnabled: false,
                focusStateEnabled: true,
                text: "Importieren",
                onClick: () => true,
            },
            {
                stylingMode: 'outlined',
                hoverStateEnabled: true,
                activeStateEnabled: false,
                focusStateEnabled: true,
                text: "Abbrechen",
                onClick: () => false,
            },
        ] : [{
                stylingMode: 'outlined',
                hoverStateEnabled: true,
                activeStateEnabled: false,
                focusStateEnabled: true,
                text: "Ok",
                onClick: () => false,
            }],
    });
    return dialog.show();
};

/**
 * Lists all executions of the state machine csvStateMachinArn
 * {{
  executions: [
    {
      executionArn: "STRING_VALUE",
      stateMachineArn: "STRING_VALUE",
      name: "STRING_VALUE",
      status: "RUNNING" || "SUCCEEDED" || "FAILED" || "TIMED_OUT" || "ABORTED",
      startDate: new Date("TIMESTAMP"),
      stopDate: new Date("TIMESTAMP"),
      mapRunArn: "STRING_VALUE",
      itemCount: Number("int"),
      stateMachineVersionArn: "STRING_VALUE",
      stateMachineAliasArn: "STRING_VALUE",
    },
  ],
  nextToken: "STRING_VALUE",
}}
 *
 */
const listExecutions = async () => {
    return client.send(new ListExecutionsCommand({ // ListExecutionsInput
        stateMachineArn: csvStateMachinArn,
        //statusFilter: "RUNNING" || "SUCCEEDED" || "FAILED" || "TIMED_OUT" || "ABORTED",
        maxResults: 1,
        nextToken: null,
        //mapRunArn: "STRING_VALUE",
    }));
}

const isUploadEnabled = async () => {
    const exec = await listExecutions();
    const [latestExec] = exec?.executions || [];
    return (!latestExec || latestExec.status !== "RUNNING");
}

/**
 * Retrieve a task (with the specified activity ARN) which has been scheduled for execution by a running state machine
 * {{
   taskToken: "STRING_VALUE",
   input: "STRING_VALUE",
 }}
 */
const getActivityTask = async () => client.send(new GetActivityTaskCommand({ // GetActivityTaskInput
    activityArn: csvStateMachineApproveActivityArn, // required
}));

/**
 * report that the task identified by the taskToken completed successfully
 * @param taskToken {String} the token that represents this task.
 * @param approvedValue {Boolean} represents the user choice
 */
const sendActivityTask = async (taskToken, approvedValue) => {
    const input = { // SendTaskSuccessInput
        taskToken: taskToken, // required
        output: JSON.stringify({approved:approvedValue}), // required
    };
    return client.send(new SendTaskSuccessCommand(input));
}

function S3FileUploader({uploadBtnText, bucketName, destFolder, allowedFileExtensions, callback, type="XML"}) {
    const [t] = useTranslation();
    const [uploadEnabled, setUploadEnabled] = useState(false);
    loadMessages(deMessages);
    locale(navigator.language);

    const uploadToS3Bucket = useCallback(async (file, progressCallback) => {
        const fullFileName = file.name;
        const dotIndex = fullFileName.lastIndexOf('.');
        const filename = fullFileName.slice(0,dotIndex).replace(/[. *+?^${}()|[\]\\]/g, "-");
        const extension = fullFileName.slice(dotIndex);
        const timestamp = new Date().getTime();
        const destFile = `${destFolder}${filename}-${timestamp}${extension}`;
        try {
            // upload
            await Storage.put(destFile, file, {
                    bucket: bucketName,
                    level: "protected",
                    progressCallback(progress) {
                        //console.log(`Uploaded: ${progress.loaded}/${progress.total}`);
                        progressCallback(progress.loaded);
                    }
                }
            );
            // get activity task
            let {taskToken, input} = await getActivityTask();
            input = JSON.parse(input);
            // confirm dialogue
            const isApproved = await confirmImportDialog(input);
            await sendActivityTask(taskToken, isApproved)

            if (typeof callback === "function") {
                callback();
            }

            if (isApproved) {
                notify(t('s3fileuploader.msg_success1'), "success", 3000);
            }
        } catch(err) {
            notify(t('s3fileuploader.msg_error1'), "error", 3000);
            console.error(err);
        }
    }, [bucketName, destFolder, callback, t]);

    const pollExecutions = useCallback(async () => {
        if (type === "CSV") {
            try {
                const enabled = await isUploadEnabled();
                setUploadEnabled(enabled);
            } catch (e) {
                console.error(e);
                setUploadEnabled(false);
            }
        } else {
            setUploadEnabled(true);
        }
    }, [type])

    useEffect(() => {
        const intervalCall = setInterval(() => {
            pollExecutions();
        }, 10000); // poll interval is 10sec
        pollExecutions();
        return () => {
            // clean up
            clearInterval(intervalCall);
        };
    }, [pollExecutions])

    return (
        <div className={"S3FileUploader"}>
            <FileUploader
                id="file-uploader"
                name={"uploader"}
                multiple={false}
                allowedFileExtensions={allowedFileExtensions}
                focusStateEnabled={false}
                uploadMode="useButtons"
                showFileList={true}
                uploadFile={uploadToS3Bucket}
                readOnly={!uploadEnabled}
            />
        </div>
    );
}

S3FileUploader.propTypes = {
    uploadBtnText: PropTypes.string.isRequired,
    bucketName:PropTypes.string.isRequired,
    destFolder: PropTypes.string.isRequired,
    allowedFileExtensions: PropTypes.array.isRequired,
    callback: PropTypes.func
}

export default S3FileUploader;